import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService, machineService, userService } from "../../../services";
import masterDataService from "../../../services/masterDataService";
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedPmSchedulesData = () => {
  try {
    const cachedSchedules = sessionStorage.getItem("pm_schedules_data");
    const cachedPlans = sessionStorage.getItem("maintenance_plans_data");
    const cachedMachines = sessionStorage.getItem("machines_data");
    const cachedSensorTags = sessionStorage.getItem("sensor_tags_data");
    const cachedTeams = sessionStorage.getItem("technician_teams_data");
    const cachedUsers = sessionStorage.getItem("users_data");

    return {
      schedules: cachedSchedules ? JSON.parse(cachedSchedules) : [],
      plans: cachedPlans ? JSON.parse(cachedPlans) : [],
      machines: cachedMachines ? JSON.parse(cachedMachines) : [],
      sensorTags: cachedSensorTags ? JSON.parse(cachedSensorTags) : [],
      teams: cachedTeams ? JSON.parse(cachedTeams) : [],
      users: cachedUsers
        ? JSON.parse(cachedUsers).filter((u) => u.status === "Active")
        : [],
      hasAll: !!(
        cachedSchedules &&
        cachedPlans &&
        cachedMachines &&
        cachedSensorTags &&
        cachedTeams &&
        cachedUsers
      ),
    };
  } catch (e) {
    console.error("Error parsing cached PM Schedules data:", e);
    return {
      schedules: [],
      plans: [],
      machines: [],
      sensorTags: [],
      teams: [],
      users: [],
      hasAll: false,
    };
  }
};

const PmSchedulesPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedPmSchedulesData();
  const [schedules, setSchedules] = useState(cachedInitialData.schedules);
  const [maintenancePlans, setMaintenancePlans] = useState(
    cachedInitialData.plans,
  );
  const [machines, setMachines] = useState(cachedInitialData.machines);
  const [sensorTags, setSensorTags] = useState(cachedInitialData.sensorTags);
  const [teams, setTeams] = useState(cachedInitialData.teams);
  const [users, setUsers] = useState(cachedInitialData.users);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState("");
  const [notificationsError, setNotificationsError] = useState("");
  const [newRecipientUserId, setNewRecipientUserId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    maintenancePlanId: "",
    assetId: "",
    startDate: "",
    endDate: "",
    nextDueDate: "",
    scheduleMode: "",
    meterThreshold: "",
    triggerSensorTagId: "",
    assignedTeamId: "",
    notifyBeforeEnabled: false,
    notifyBeforeDays: "",
    notifyOnOverdue: false,
    notifyOnCompleted: false,
    notifyRecipientUserIds: [],
    autoCreateWorkOrder: false,
    active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Mark Completed state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingSchedule, setCompletingSchedule] = useState(null);
  const [completePlanTasks, setCompletePlanTasks] = useState([]);
  const [completeForm, setCompleteForm] = useState({
    executedDate: "",
    executedBy: "",
    findings: "",
    elapsedMinutes: "",
    taskResults: {},
    nextDueDate: "", // fallback if not computable from plan frequency
  });
  const [completeErrors, setCompleteErrors] = useState({});
  const [completing, setCompleting] = useState(false);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadData(!hasCachedData);
    } else {
      setError("Please login to view PM schedules");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const [
        schedulesData,
        plansData,
        machinesData,
        tagsData,
        teamsData,
        usersData,
      ] = await Promise.all([
        masterDataService.getPMSchedules(tenantId),
        masterDataService.getMaintenancePlans(tenantId),
        machineService.getMachines(tenantId),
        masterDataService.getSensorTags(tenantId),
        masterDataService.getTechnicianTeams(tenantId),
        userService.getAllUsers().catch(() => []),
      ]);
      setSchedules(schedulesData || []);
      setMaintenancePlans(plansData || []);
      setMachines(machinesData || []);
      setSensorTags(tagsData || []);
      setTeams(teamsData || []);
      const activeUsers = (usersData || []).filter(
        (u) => u.status === "Active",
      );
      setUsers(activeUsers);

      // Cache the data
      sessionStorage.setItem(
        "pm_schedules_data",
        JSON.stringify(schedulesData || []),
      );
      sessionStorage.setItem(
        "maintenance_plans_data",
        JSON.stringify(plansData || []),
      );
      sessionStorage.setItem(
        "machines_data",
        JSON.stringify(machinesData || []),
      );
      sessionStorage.setItem(
        "sensor_tags_data",
        JSON.stringify(tagsData || []),
      );
      sessionStorage.setItem(
        "technician_teams_data",
        JSON.stringify(teamsData || []),
      );
      sessionStorage.setItem("users_data", JSON.stringify(usersData || []));
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setSchedules([]);
        setMaintenancePlans([]);
        setMachines([]);
        setSensorTags([]);
        setTeams([]);
        setUsers([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSchedules = schedules.filter((schedule) => {
    const plan = maintenancePlans.find(
      (p) => p.maintenancePlanId === schedule.maintenancePlanId,
    );
    const machine = machines.find((m) => m.machineId === schedule.assetId);
    return (
      plan?.planName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine?.machineName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleAdd = () => {
    setEditingSchedule(null);
    setFormData({
      maintenancePlanId: "",
      assetId: "",
      startDate: "",
      endDate: "",
      nextDueDate: "",
      scheduleMode: "",
      meterThreshold: "",
      triggerSensorTagId: "",
      assignedTeamId: "",
      notifyBeforeEnabled: false,
      notifyBeforeDays: "",
      notifyOnOverdue: false,
      notifyOnCompleted: false,
      notifyRecipientUserIds: [],
      autoCreateWorkOrder: false,
      active: true,
    });
    setFormErrors({});
    setNotificationsError("");
    setNewRecipientUserId("");
    setShowModal(true);
  };

  const parseNotificationsConfig = (raw) => {
    const defaults = {
      notifyBeforeEnabled: false,
      notifyBeforeDays: "",
      notifyOnOverdue: false,
      notifyOnCompleted: false,
      notifyRecipientUserIds: [],
    };

    if (!raw || typeof raw !== "string") return defaults;
    try {
      const cfg = JSON.parse(raw);
      const days = cfg?.notifyBeforeDays;
      const recipients = Array.isArray(cfg?.recipientUserIds)
        ? cfg.recipientUserIds
        : [];
      return {
        notifyBeforeEnabled: Number.isFinite(Number(days)) && Number(days) > 0,
        notifyBeforeDays:
          Number.isFinite(Number(days)) && Number(days) > 0
            ? String(parseInt(days, 10))
            : "",
        notifyOnOverdue: Boolean(cfg?.notifyOnOverdue),
        notifyOnCompleted: Boolean(cfg?.notifyOnCompleted),
        notifyRecipientUserIds: recipients.filter(
          (x) => Number.isInteger(x) && x > 0,
        ),
      };
    } catch {
      return defaults;
    }
  };

  const buildNotificationsConfigJson = (fd) => {
    const recipientUserIds = (fd.notifyRecipientUserIds || []).filter(
      (x) => Number.isInteger(x) && x > 0,
    );
    const notifyBeforeDays =
      fd.notifyBeforeEnabled && fd.notifyBeforeDays
        ? parseInt(fd.notifyBeforeDays, 10)
        : null;

    const cfg = {};
    if (recipientUserIds.length > 0) cfg.recipientUserIds = recipientUserIds;
    if (notifyBeforeDays && notifyBeforeDays > 0)
      cfg.notifyBeforeDays = notifyBeforeDays;
    if (fd.notifyOnOverdue) cfg.notifyOnOverdue = true;
    if (fd.notifyOnCompleted) cfg.notifyOnCompleted = true;

    return Object.keys(cfg).length === 0 ? null : JSON.stringify(cfg);
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toISOString().split("T")[0];
    };

    const notif = parseNotificationsConfig(
      typeof schedule.notificationsConfigJson === "string"
        ? schedule.notificationsConfigJson
        : schedule.notificationsConfigJson
          ? JSON.stringify(schedule.notificationsConfigJson)
          : "",
    );

    setFormData({
      maintenancePlanId: schedule.maintenancePlanId?.toString() || "",
      assetId: schedule.assetId?.toString() || "",
      startDate: formatDate(schedule.startDate),
      endDate: formatDate(schedule.endDate),
      nextDueDate: formatDate(schedule.nextDueDate),
      scheduleMode: typeof schedule.scheduleMode === "object" ? (schedule.scheduleMode?.ScheduleMode || schedule.scheduleMode?.scheduleMode || "Cron") : schedule.scheduleMode || "Cron",
      meterThreshold: schedule.meterThreshold?.toString() || "",
      triggerSensorTagId: schedule.triggerSensorTagId?.toString() || "",
      assignedTeamId: schedule.assignedTeamId?.toString() || "",
      ...notif,
      autoCreateWorkOrder: schedule.autoCreateWorkOrder || false,
      active: schedule.active !== undefined ? schedule.active : true,
    });
    setFormErrors({});
    setNotificationsError("");
    setNewRecipientUserId("");
    setShowModal(true);
  };

  const computeNextDueDate = (executedDateStr, plan) => {
    if (!executedDateStr || !plan) return null;

    const freqType = (plan.frequencyType || "").toString().trim().toLowerCase();
    const freqValue = plan.frequencyValue;
    const freqUnit = (plan.frequencyUnit || "").toString().trim().toLowerCase();

    if (freqType !== "time") return null;
    if (!freqValue || Number(freqValue) <= 0) return null;
    if (!freqUnit) return null;

    const executed = new Date(executedDateStr);
    if (Number.isNaN(executed.getTime())) return null;

    const next = new Date(executed);
    const v = Number(freqValue);

    if (freqUnit.startsWith("day")) {
      next.setDate(next.getDate() + v);
      return next;
    }
    if (freqUnit.startsWith("hour")) {
      next.setHours(next.getHours() + v);
      return next;
    }
    if (freqUnit.startsWith("week")) {
      next.setDate(next.getDate() + 7 * v);
      return next;
    }
    if (freqUnit.startsWith("month")) {
      next.setMonth(next.getMonth() + v);
      return next;
    }
    if (freqUnit.startsWith("year")) {
      next.setFullYear(next.getFullYear() + v);
      return next;
    }

    return null;
  };

  const openComplete = async (schedule) => {
    try {
      setError("");
      setCompleteErrors({});
      setCompletingSchedule(schedule);
      setCompletePlanTasks([]);

      const user = authService.getUser();
      const today = new Date().toISOString().split("T")[0];

      const tasks = await masterDataService.getMaintenancePlanTasks(
        tenantId,
        schedule.maintenancePlanId,
      );
      const taskResults = (tasks || []).reduce((acc, t) => {
        acc[t.taskId] = "";
        return acc;
      }, {});

      const plan = maintenancePlans.find(
        (p) => p.maintenancePlanId === schedule.maintenancePlanId,
      );
      const nextDue = computeNextDueDate(today, plan);

      setCompletePlanTasks(tasks || []);
      setCompleteForm({
        executedDate: today,
        executedBy: user?.fullName || user?.username || "",
        findings: "",
        elapsedMinutes: "",
        taskResults,
        nextDueDate: nextDue ? nextDue.toISOString().split("T")[0] : "",
      });
      setShowCompleteModal(true);
    } catch (err) {
      setError("Failed to load plan tasks for completion. Please try again.");
      console.error("Error opening completion modal:", err);
    }
  };

  const closeComplete = () => {
    setShowCompleteModal(false);
    setCompletingSchedule(null);
    setCompletePlanTasks([]);
    setCompleteErrors({});
    setCompleting(false);
    setCompleteForm({
      executedDate: "",
      executedBy: "",
      findings: "",
      elapsedMinutes: "",
      taskResults: {},
      nextDueDate: "",
    });
  };

  const validateComplete = () => {
    const errors = {};
    if (!completeForm.executedDate) {
      errors.executedDate = "Executed date is required";
    }

    // Mandatory tasks must have results
    const missingMandatory = (completePlanTasks || [])
      .filter((t) => t.isMandatory)
      .filter(
        (t) => !String(completeForm.taskResults?.[t.taskId] ?? "").trim(),
      );

    if (missingMandatory.length > 0) {
      errors.mandatoryTasks = "Please fill all mandatory tasks";
      missingMandatory.forEach((t) => {
        errors[`task_${t.taskId}`] = "Required";
      });
    }

    // Next due date fallback if plan frequency cannot compute
    const plan = maintenancePlans.find(
      (p) => p.maintenancePlanId === completingSchedule?.maintenancePlanId,
    );
    const computed = computeNextDueDate(completeForm.executedDate, plan);
    if (!computed && !completeForm.nextDueDate) {
      errors.nextDueDate =
        "Next due date is required (plan frequency is not sufficient to compute)";
    }

    setCompleteErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitComplete = async (e) => {
    e.preventDefault();
    if (!completingSchedule) return;
    if (!validateComplete()) return;

    try {
      setCompleting(true);
      setError("");

      const plan = maintenancePlans.find(
        (p) => p.maintenancePlanId === completingSchedule.maintenancePlanId,
      );
      const computed = computeNextDueDate(completeForm.executedDate, plan);

      const taskResults = (completePlanTasks || []).map((t) => ({
        taskId: t.taskId,
        result: String(completeForm.taskResults?.[t.taskId] ?? "").trim(),
      }));

      const payload = {
        tenantId,
        executedDate: new Date(completeForm.executedDate),
        executedBy: completeForm.executedBy.trim() || null,
        findings: completeForm.findings.trim() || null,
        elapsedMinutes: completeForm.elapsedMinutes
          ? parseInt(completeForm.elapsedMinutes, 10)
          : null,
        taskResults,
      };

      if (!computed && completeForm.nextDueDate) {
        payload.nextDueDate = new Date(completeForm.nextDueDate);
      }

      await masterDataService.completePMSchedule(
        completingSchedule.pmScheduleId,
        payload,
      );
      await loadData();
      closeComplete();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to complete PM schedule. Please try again.";
      setError(errorMessage);
    } finally {
      setCompleting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deletePMSchedule(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete PM schedule. Please try again.");
      console.error("Error deleting PM schedule:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.maintenancePlanId) {
      errors.maintenancePlanId = "Maintenance plan is required";
    }
    if (!formData.assetId) {
      errors.assetId = "Machine is required";
    }
    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!formData.nextDueDate) {
      errors.nextDueDate = "Next due date is required";
    }

    const wantsNotifications =
      formData.notifyBeforeEnabled ||
      formData.notifyOnOverdue ||
      formData.notifyOnCompleted;
    const recipientsCount = Array.isArray(formData.notifyRecipientUserIds)
      ? formData.notifyRecipientUserIds.length
      : 0;

    if (formData.notifyBeforeEnabled) {
      const days = parseInt(formData.notifyBeforeDays, 10);
      if (!days || days <= 0) {
        errors.notifyBeforeDays = "Enter days > 0";
      }
    }

    if (wantsNotifications && recipientsCount === 0) {
      errors.notifyRecipientUserIds = "Select at least one recipient user";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const scheduleData = {
        tenantId: tenantId,
        maintenancePlanId: parseInt(formData.maintenancePlanId),
        assetId: parseInt(formData.assetId),
        startDate: new Date(formData.startDate),
        endDate: formData.endDate ? new Date(formData.endDate) : null,
        nextDueDate: new Date(formData.nextDueDate),
        scheduleMode: typeof formData.scheduleMode === "string" ? formData.scheduleMode.trim() || "Cron" : "Cron",
        meterThreshold: formData.meterThreshold
          ? parseFloat(formData.meterThreshold)
          : null,
        triggerSensorTagId: formData.triggerSensorTagId
          ? parseInt(formData.triggerSensorTagId)
          : null,
        assignedTeamId: formData.assignedTeamId
          ? parseInt(formData.assignedTeamId)
          : null,
        notificationsConfigJson: buildNotificationsConfigJson(formData),
        autoCreateWorkOrder: formData.autoCreateWorkOrder,
        active: formData.active,
      };

      if (editingSchedule) {
        scheduleData.pmScheduleId = editingSchedule.pmScheduleId;
        await masterDataService.updatePMSchedule(
          editingSchedule.pmScheduleId,
          scheduleData,
        );
      } else {
        await masterDataService.createPMSchedule(scheduleData);
      }

      await loadData();
      setShowModal(false);
      setFormData({
        maintenancePlanId: "",
        assetId: "",
        startDate: "",
        endDate: "",
        nextDueDate: "",
        scheduleMode: "",
        meterThreshold: "",
        triggerSensorTagId: "",
        assignedTeamId: "",
        notifyBeforeEnabled: false,
        notifyBeforeDays: "",
        notifyOnOverdue: false,
        notifyOnCompleted: false,
        notifyRecipientUserIds: [],
        autoCreateWorkOrder: false,
        active: true,
      });
      setEditingSchedule(null);
      setNotificationsError("");
      setNewRecipientUserId("");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save PM schedule. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
    setFormData({
      maintenancePlanId: "",
      assetId: "",
      startDate: "",
      endDate: "",
      nextDueDate: "",
      scheduleMode: "",
      meterThreshold: "",
      triggerSensorTagId: "",
      assignedTeamId: "",
      notifyBeforeEnabled: false,
      notifyBeforeDays: "",
      notifyOnOverdue: false,
      notifyOnCompleted: false,
      notifyRecipientUserIds: [],
      autoCreateWorkOrder: false,
      active: true,
    });
    setFormErrors({});
    setNotificationsError("");
    setNewRecipientUserId("");
  };

  const getUserLabel = (userId) => {
    if (!userId) return "-";
    const u = users.find((x) => x.userId === userId);
    return u ? `${u.fullName} (${u.username})` : userId;
  };

  const addRecipient = () => {
    setNotificationsError("");
    if (!newRecipientUserId) {
      setNotificationsError("Please select a user to add.");
      return;
    }
    const userId = parseInt(newRecipientUserId, 10);
    if (!userId || userId <= 0) {
      setNotificationsError("Invalid user selected.");
      return;
    }
    if ((formData.notifyRecipientUserIds || []).includes(userId)) {
      setNotificationsError("User already added.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      notifyRecipientUserIds: [...(prev.notifyRecipientUserIds || []), userId],
    }));
    setNewRecipientUserId("");
  };

  const removeRecipient = (userId) => {
    setNotificationsError("");
    setFormData((prev) => ({
      ...prev,
      notifyRecipientUserIds: (prev.notifyRecipientUserIds || []).filter(
        (id) => id !== userId,
      ),
    }));
  };

  const getPlanName = (planId) => {
    if (!planId) return "-";
    const plan = maintenancePlans.find((p) => p.maintenancePlanId === planId);
    return plan ? `${plan.planCode} - ${plan.planName}` : "-";
  };

  const getMachineName = (machineId) => {
    if (!machineId) return "-";
    const machine = machines.find((m) => m.machineId === machineId);
    return machine ? `${machine.machineCode} - ${machine.machineName}` : "-";
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading PM schedules..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>PM Schedules</h1>
          <p>Manage PM schedules for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Schedule
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search schedules..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredSchedules.length}{" "}
            {filteredSchedules.length === 1 ? "schedule" : "schedules"}
          </div>
        </div>

        {filteredSchedules.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClock className="empty-icon" />
            <h3>No schedules found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first PM schedule"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Schedule
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan</th>
                <th>Machine</th>
                <th>Next Due Date</th>
                <th>Mode</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchedules.map((schedule) => (
                <tr key={schedule.pmScheduleId}>
                  <td>{schedule.pmScheduleId}</td>
                  <td>{getPlanName(schedule.maintenancePlanId)}</td>
                  <td>{getMachineName(schedule.assetId)}</td>
                  <td>
                    {schedule.nextDueDate
                      ? new Date(schedule.nextDueDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{schedule.scheduleMode || "-"}</td>
                  <td>{schedule.active ? "Yes" : "No"}</td>
                  <td>
                    <div className="action-buttons">
                      {schedule.active && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => openComplete(schedule)}
                          title="Mark Completed"
                        >
                          <HiOutlineCheckCircle />
                        </button>
                      )}
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(schedule)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(schedule)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingSchedule ? "Edit PM Schedule" : "Add New PM Schedule"}
        customWidth="800px"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="pm-schedule-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingSchedule
                  ? "Updating..."
                  : "Creating..."
                : editingSchedule
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="pm-schedule-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="maintenancePlanId">
                Plan <span className="required">*</span>
              </label>
              <select
                id="maintenancePlanId"
                value={formData.maintenancePlanId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maintenancePlanId: e.target.value,
                  })
                }
                className={formErrors.maintenancePlanId ? "error" : ""}
              >
                <option value="">Select Plan</option>
                {maintenancePlans.map((plan) => (
                  <option
                    key={plan.maintenancePlanId}
                    value={plan.maintenancePlanId.toString()}
                  >
                    {plan.planCode} - {plan.planName}
                  </option>
                ))}
              </select>
              {formErrors.maintenancePlanId && (
                <span className="field-error">
                  {formErrors.maintenancePlanId}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="assetId">
                Machine <span className="required">*</span>
              </label>
              <select
                id="assetId"
                value={formData.assetId}
                onChange={(e) =>
                  setFormData({ ...formData, assetId: e.target.value })
                }
                className={formErrors.assetId ? "error" : ""}
              >
                <option value="">Select Machine</option>
                {machines.map((machine) => (
                  <option
                    key={machine.machineId}
                    value={machine.machineId.toString()}
                  >
                    {machine.machineCode} - {machine.machineName}
                  </option>
                ))}
              </select>
              {formErrors.assetId && (
                <span className="field-error">{formErrors.assetId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="startDate">
                Start Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                className={formErrors.startDate ? "error" : ""}
              />
              {formErrors.startDate && (
                <span className="field-error">{formErrors.startDate}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="nextDueDate">
                Next Due Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="nextDueDate"
                value={formData.nextDueDate}
                onChange={(e) =>
                  setFormData({ ...formData, nextDueDate: e.target.value })
                }
                className={formErrors.nextDueDate ? "error" : ""}
              />
              {formErrors.nextDueDate && (
                <span className="field-error">{formErrors.nextDueDate}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="scheduleMode">Schedule Mode</label>
              <input
                type="text"
                id="scheduleMode"
                value={formData.scheduleMode}
                onChange={(e) =>
                  setFormData({ ...formData, scheduleMode: e.target.value })
                }
                placeholder="Enter schedule mode (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="meterThreshold">Meter Threshold</label>
              <input
                type="number"
                id="meterThreshold"
                value={formData.meterThreshold}
                onChange={(e) =>
                  setFormData({ ...formData, meterThreshold: e.target.value })
                }
                placeholder="Enter meter threshold (optional)"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="triggerSensorTagId">Trigger Sensor Tag</label>
              <select
                id="triggerSensorTagId"
                value={formData.triggerSensorTagId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    triggerSensorTagId: e.target.value,
                  })
                }
              >
                <option value="">Select Sensor Tag (Optional)</option>
                {sensorTags.map((tag) => (
                  <option key={tag.tagId} value={tag.tagId.toString()}>
                    {tag.tagName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="assignedTeamId">Assigned Team</label>
              <select
                id="assignedTeamId"
                value={formData.assignedTeamId}
                onChange={(e) =>
                  setFormData({ ...formData, assignedTeamId: e.target.value })
                }
              >
                <option value="">Select Team (Optional)</option>
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId.toString()}>
                    {team.teamName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Notifications</label>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.5rem",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.notifyBeforeEnabled}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notifyBeforeEnabled: e.target.checked,
                          notifyBeforeDays: e.target.checked
                            ? prev.notifyBeforeDays || "1"
                            : "",
                        }))
                      }
                    />
                    Notify before due
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.notifyBeforeDays}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notifyBeforeDays: e.target.value,
                      }))
                    }
                    placeholder="Days before"
                    disabled={!formData.notifyBeforeEnabled}
                    className={formErrors.notifyBeforeDays ? "error" : ""}
                  />
                </div>
                {formErrors.notifyBeforeDays && (
                  <span className="field-error">
                    {formErrors.notifyBeforeDays}
                  </span>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.5rem",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.notifyOnOverdue}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notifyOnOverdue: e.target.checked,
                        }))
                      }
                    />
                    Notify on overdue
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.notifyOnCompleted}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notifyOnCompleted: e.target.checked,
                        }))
                      }
                    />
                    Notify on completed
                  </label>
                </div>

                <div>
                  <label>Recipients</label>
                  {notificationsError && (
                    <div className="error-message">{notificationsError}</div>
                  )}
                  {formErrors.notifyRecipientUserIds && (
                    <div className="error-message">
                      {formErrors.notifyRecipientUserIds}
                    </div>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr",
                      gap: "0.5rem",
                      marginTop: "0.5rem",
                    }}
                  >
                    <select
                      value={newRecipientUserId}
                      onChange={(e) => setNewRecipientUserId(e.target.value)}
                    >
                      <option value="">Select User</option>
                      {users.map((u) => (
                        <option key={u.userId} value={u.userId.toString()}>
                          {u.fullName} ({u.username})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={addRecipient}
                    >
                      <HiOutlineCheckCircle />
                      Add
                    </button>
                  </div>

                  {(formData.notifyRecipientUserIds || []).length === 0 ? (
                    <div
                      className="empty-state"
                      style={{ padding: "1rem", marginTop: "0.75rem" }}
                    >
                      No recipients selected.
                    </div>
                  ) : (
                    <table
                      className="data-table"
                      style={{ marginTop: "0.75rem" }}
                    >
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.notifyRecipientUserIds || []).map(
                          (uid) => (
                            <tr key={uid}>
                              <td>{getUserLabel(uid)}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    type="button"
                                    className="btn-icon-delete"
                                    onClick={() => removeRecipient(uid)}
                                    title="Remove"
                                  >
                                    <HiOutlineTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="autoCreateWorkOrder">
                <input
                  type="checkbox"
                  id="autoCreateWorkOrder"
                  checked={formData.autoCreateWorkOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      autoCreateWorkOrder: e.target.checked,
                    })
                  }
                />
                Auto Create Work Order
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="active">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) =>
                    setFormData({ ...formData, active: e.target.checked })
                  }
                />
                Active
              </label>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Mark Completed Modal */}
      <SharedModal
        isOpen={showCompleteModal && !!completingSchedule}
        onClose={closeComplete}
        title="Mark PM Schedule Completed"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={closeComplete}
              disabled={completing}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="complete-pm-form"
              className="btn-primary"
              disabled={completing}
            >
              {completing ? "Completing..." : "Mark Completed"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          {completeErrors.mandatoryTasks && (
            <div className="error-message">
              {completeErrors.mandatoryTasks}
            </div>
          )}

          <form id="complete-pm-form" className="modal-form" onSubmit={submitComplete}>
            <div className="form-group">
              <label htmlFor="pmCompleteExecutedDate">
                Executed Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="pmCompleteExecutedDate"
                value={completeForm.executedDate}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  const plan = maintenancePlans.find(
                    (p) =>
                      p.maintenancePlanId ===
                      completingSchedule.maintenancePlanId,
                  );
                  const computed = computeNextDueDate(nextDate, plan);
                  setCompleteForm((p) => ({
                    ...p,
                    executedDate: nextDate,
                    nextDueDate: computed
                      ? computed.toISOString().split("T")[0]
                      : p.nextDueDate,
                  }));
                }}
                className={completeErrors.executedDate ? "error" : ""}
              />
              {completeErrors.executedDate && (
                <span className="field-error">
                  {completeErrors.executedDate}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="pmCompleteExecutedBy">Executed By</label>
              <input
                type="text"
                id="pmCompleteExecutedBy"
                value={completeForm.executedBy}
                onChange={(e) =>
                  setCompleteForm((p) => ({
                    ...p,
                    executedBy: e.target.value,
                  }))
                }
                placeholder="Technician name (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pmCompleteElapsedMinutes">
                Elapsed Minutes
              </label>
              <input
                type="number"
                id="pmCompleteElapsedMinutes"
                value={completeForm.elapsedMinutes}
                onChange={(e) =>
                  setCompleteForm((p) => ({
                    ...p,
                    elapsedMinutes: e.target.value,
                  }))
                }
                placeholder="e.g., 60"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pmCompleteFindings">Findings</label>
              <textarea
                id="pmCompleteFindings"
                value={completeForm.findings}
                onChange={(e) =>
                  setCompleteForm((p) => ({ ...p, findings: e.target.value }))
                }
                placeholder="Notes / findings (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <label style={{ margin: 0 }}>Checklist</label>
                <button
                  type="button"
                  className="btn-primary"
                  style={{
                    padding: "0.4rem 1rem",
                    fontSize: "0.8rem",
                    minHeight: "auto",
                  }}
                >
                  <HiOutlinePlus />
                  New CAPA
                </button>
              </div>
              {completePlanTasks.length === 0 ? (
                <div className="empty-state" style={{ padding: "1rem" }}>
                  No tasks defined for this plan.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {completePlanTasks.map((t) => (
                    <div
                      key={t.taskId}
                      style={{
                        border: "1px solid var(--border-primary)",
                        borderRadius: 10,
                        padding: "0.75rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "1rem",
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {t.taskDescription}
                        </div>
                        {t.isMandatory && <span className="required">*</span>}
                      </div>
                      <textarea
                        value={completeForm.taskResults?.[t.taskId] ?? ""}
                        onChange={(e) =>
                          setCompleteForm((p) => ({
                            ...p,
                            taskResults: {
                              ...(p.taskResults || {}),
                              [t.taskId]: e.target.value,
                            },
                          }))
                        }
                        rows="2"
                        placeholder="Result / remarks"
                        className={
                          completeErrors[`task_${t.taskId}`] ? "error" : ""
                        }
                        style={{ marginTop: "0.5rem" }}
                      />
                      {completeErrors[`task_${t.taskId}`] && (
                        <span className="field-error">
                          {completeErrors[`task_${t.taskId}`]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Next Due Date preview / fallback */}
            <div className="form-group">
              <label htmlFor="pmCompleteNextDueDate">
                Next Due Date
                {completeErrors.nextDueDate ? (
                  <span className="required">*</span>
                ) : null}
              </label>
              <input
                type="date"
                id="pmCompleteNextDueDate"
                value={completeForm.nextDueDate}
                onChange={(e) =>
                  setCompleteForm((p) => ({
                    ...p,
                    nextDueDate: e.target.value,
                  }))
                }
                className={completeErrors.nextDueDate ? "error" : ""}
              />
              {completeErrors.nextDueDate && (
                <span className="field-error">
                  {completeErrors.nextDueDate}
                </span>
              )}
              <div
                style={{
                  color: "var(--text-tertiary)",
                  fontSize: "0.85rem",
                  marginTop: "0.25rem",
                }}
              >
                By default, this is calculated as Executed Date + Plan
                frequency.
              </div>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.pmScheduleId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete this PM schedule?</p>
          <p className="warning-text">This action cannot be undone.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PmSchedulesPage;
